(() => {
  'use strict';
  if (!window.customElements || customElements.get('km-cart-pair-dialog')) return;

  class KMCartPairDialog extends HTMLElement {
    connectedCallback() {
      this.cartFormId = this.dataset.cartFormId || 'CartDrawerForm';
      this.scopeSelector = this.cartFormId === 'CartPageForm' ? '#CartPage' : '#CartDrawer';
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.productTemplates = new Map([...this.querySelectorAll('[data-km-pair-product-template]')]
        .map(template => [template.dataset.kmPairProductTemplate, template]));
      this.media = window.matchMedia('(min-width: 769px)');
      this.onClick = event => {
        const retry = event.target.closest('[data-km-pair-retry]');
        if (retry && retry.closest(this.scopeSelector)) {
          event.preventDefault();
          this.add(true);
          return;
        }
        const opener = event.target.closest('[data-km-pair-open]');
        if (opener && opener.closest(this.scopeSelector)) {
          event.preventDefault();
          this.open(opener);
        }
      };
      this.onDialogClick = event => {
        event.stopPropagation();
        if (event.target === this || event.target.closest('[data-km-pair-close]')) this.close();
        if (event.target.closest('[data-km-pair-detail-retry]')) this.loadProduct(this.requestedHandle);
        const option = event.target.closest('[data-km-pair-variant]');
        if (option && !option.disabled && !this.busy && !this.rebuildOnly) this.select(option.dataset.kmPairVariant);
        if (event.target.closest('[data-km-pair-add]')) this.add();
        if (event.target.closest('[data-km-pair-gallery-prev]')) this.showImage((this.imageIndex || 0) - 1);
        if (event.target.closest('[data-km-pair-gallery-next]')) this.showImage((this.imageIndex || 0) + 1);
      };
      this.onKey = event => {
        if (event.type === 'keyup' && event.key === 'Escape' && this.suppressEscape) {
          this.suppressEscape = false;
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (this.hidden || event.type !== 'keydown') return;
        if (event.key === 'Escape') {
          this.suppressEscape = true;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.close();
        } else if (event.key === 'Tab') {
          const controls = [...this.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')]
            .filter(node => node.getClientRects().length);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (first && (!controls.includes(document.activeElement)
            || event.shiftKey && document.activeElement === first
            || !event.shiftKey && document.activeElement === last)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            (event.shiftKey ? last : first).focus();
          }
        }
      };
      this.onMedia = () => this.close();
      this.onSubmit = event => {
        if (event.target.id !== this.cartFormId || !this.pending) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      this.onContext = event => { if (!event.detail?.item) this.close(); };
      this.onGiftOpen = () => this.close();
      document.addEventListener('click', this.onClick);
      this.addEventListener('click', this.onDialogClick);
      document.addEventListener('keydown', this.onKey, true);
      document.addEventListener('keyup', this.onKey, true);
      window.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('km:cart-pair-context', this.onContext);
      document.addEventListener('km:cart-gift-dialog-open', this.onGiftOpen);
      this.media.addEventListener('change', this.onMedia);
    }

    disconnectedCallback() {
      this.close();
      document.removeEventListener('click', this.onClick);
      this.removeEventListener('click', this.onDialogClick);
      document.removeEventListener('keydown', this.onKey, true);
      document.removeEventListener('keyup', this.onKey, true);
      window.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('km:cart-pair-context', this.onContext);
      document.removeEventListener('km:cart-gift-dialog-open', this.onGiftOpen);
      this.media?.removeEventListener('change', this.onMedia);
    }

    removeFocus(namespace, container) {
      window.theme?.a11y?.removeTrapFocus({container, namespace});
      document.documentElement.off?.('focusout.' + namespace);
      document.documentElement.off?.('keydown.' + namespace);
    }

    open(opener) {
      if (!this.hidden) return;
      const handle = opener.dataset.kmPairOpen;
      if (this.pending && handle !== this.dataset.productHandle) return;
      if (!/^[a-z0-9][a-z0-9-]*$/i.test(handle || '')) return;
      const bogoId = opener.dataset.kmBogoActivity || '';
      const bogoAnchor = opener.dataset.kmBogoAnchor || '';
      if (this.pending && bogoId !== (this.bogoActivityId || '')) return;
      if (this.pending && bogoAnchor !== (this.bogoAnchor || '')) return;
      if (bogoId && !window.KMCartBogo?.getActivity(bogoId)) return;
      if (bogoId !== (this.bogoActivityId || '')) this.dataset.productHandle = '';
      this.bogoActivityId = bogoId;
      this.bogoAnchor = bogoAnchor;
      this.productNotes = (opener.dataset.kmPairNotes || '').trim();
      this.returnFocus = opener;
      this.drawerInstance = this.cartFormId === 'CartDrawerForm' ? window.KMCartGifts?.getDrawer?.() : null;
      const drawer = this.drawerInstance?.drawer;
      if (drawer?.isOpen) {
        drawer.unbindEvents();
        this.removeFocus('drawer_focus', drawer.drawer);
        this.removeFocus('cartdrawer_focus', this.drawerInstance.form);
      }
      this.hidden = false;
      if (this.bogoActivityId) {
        const gift = window.KMCartBogo.getActivity(this.bogoActivityId)?.products.find(product => product.handle === handle);
        window.KMCartAnalytics?.commerce?.('view_buy1_get1', {product_id: gift?.id});
      }
      document.documentElement.classList.add('km-pair-dialog-open');
      this.querySelector('[data-km-pair-panel]').focus();
      this.loadProduct(handle);
    }

    async loadProduct(handle) {
      const revision = this.loadRevision = (this.loadRevision || 0) + 1;
      this.requestedHandle = handle;
      this.loadController?.abort();
      const body = this.querySelector('[data-km-pair-detail-body]');
      const error = this.querySelector('[data-km-pair-detail-error]');
      error.hidden = true;
      if (handle === this.dataset.productHandle) {
        this.renderNotes();
        this.updateBogoButton();
        return;
      }
      this.dataset.productHandle = '';
      body.replaceChildren();
      body.classList.add('is-loading');
      body.setAttribute('aria-busy', 'true');
      this.variants = [];
      const controller = new AbortController();
      this.loadController = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.root + 'products/' + encodeURIComponent(handle) + '?section_id=cart-drawer-pair-product', {
          credentials: 'same-origin', signal: controller.signal
        });
        if (!response.ok) throw new Error('Unable to load product.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = html.querySelector('[data-km-pair-product-template]');
        if (revision !== this.loadRevision || this.hidden) return;
        if (template?.dataset.kmPairProductTemplate !== handle || !template.content.querySelector('[data-km-pair-variants]')) {
          throw new Error('Product is unavailable.');
        }
        this.productTemplates.clear();
        this.productTemplates.set(handle, template);
        this.setProduct(handle);
      } catch (failure) {
        if (revision === this.loadRevision && !this.hidden) error.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (revision === this.loadRevision) {
          body.classList.remove('is-loading');
          body.setAttribute('aria-busy', 'false');
        }
      }
    }

    setProduct(handle) {
      if (handle === this.dataset.productHandle) return true;
      const template = this.productTemplates.get(handle);
      if (!template) return false;
      this.querySelector('[data-km-pair-detail-body]').replaceChildren(template.content.cloneNode(true));
      this.renderNotes();
      this.dataset.productHandle = handle;
      this.dataset.variantId = this.querySelector('[data-km-pair-default-variant]').dataset.kmPairDefaultVariant;
      this.variants = JSON.parse(this.querySelector('[data-km-pair-variants]').textContent);
      this.button = this.querySelector('[data-km-pair-add]');
      this.error = this.querySelector('[data-km-pair-error]');
      if (this.bogoActivityId) {
        const offer = window.KMCartBogo.getActivity(this.bogoActivityId);
        const product = offer?.products.find(product => product.handle === handle);
        const allowed = new Set(window.KMCartBogoRules.variants(offer, product || {}).map(variant => String(variant.id)));
        this.variants.forEach(variant => { variant.available = variant.available && allowed.has(String(variant.id)); });
        this.querySelectorAll('[data-km-pair-variant]').forEach(option => {
          option.disabled = !this.variants.some(variant => String(variant.id) === option.dataset.kmPairVariant && variant.available);
        });
        const first = this.variants.find(variant => variant.available && window.KMCartBogo.canAdd(this.bogoActivityId, variant.id, undefined, this.bogoAnchor))
          || this.variants.find(variant => variant.available);
        if (first) this.select(first.id);
        this.updateBogoButton();
      }
      this.imageIndex = 0;
      return true;
    }

    renderNotes() {
      this.querySelector('[data-km-pair-notes]')?.remove();
      const title = this.querySelector('.km-gift-detail__content h3');
      if (!this.productNotes || !title) return;
      const notes = document.createElement('p');
      notes.className = 'km-pair-detail__notes';
      notes.setAttribute('data-km-pair-notes', '');
      const icon = document.createElement('img');
      icon.src = 'https://cdn.shopify.com/s/files/1/2234/5357/files/e76ede38f3997c9ba3c6b5a0ba271a86.svg?v=1792398384';
      icon.alt = '';
      icon.width = 16;
      icon.height = 16;
      const text = document.createElement('span');
      text.textContent = this.productNotes;
      notes.append(icon, text);
      title.before(notes);
    }

    close() {
      if (this.hidden) return;
      this.loadRevision = (this.loadRevision || 0) + 1;
      this.loadController?.abort();
      this.hidden = true;
      document.documentElement.classList.remove('km-pair-dialog-open');
      if (!this.returnFocus?.isConnected && this.bogoActivityId) {
        this.returnFocus = [...document.querySelectorAll(`${this.scopeSelector} [data-km-bogo-activity]`)]
          .find(button => button.dataset.kmBogoActivity === this.bogoActivityId && button.dataset.kmBogoAnchor === this.bogoAnchor && button.dataset.kmPairOpen === this.requestedHandle);
      }
      const drawer = this.drawerInstance?.drawer;
      const giftOpen = document.querySelector('[data-km-gift-dialog]:not([hidden])');
      if (!giftOpen && drawer?.isOpen) {
        drawer.bindEvents();
        window.theme?.a11y?.trapFocus({container: this.drawerInstance.form,
          elementToFocus: this.returnFocus?.isConnected ? this.returnFocus : this.drawerInstance.form,
          namespace: 'cartdrawer_focus'});
      }
      if (!giftOpen && this.returnFocus?.isConnected) this.returnFocus.focus();
    }

    select(id) {
      const variant = this.variants.find(variant => String(variant.id) === String(id) && variant.available);
      if (!variant) return;
      this.dataset.variantId = String(variant.id);
      this.querySelectorAll('[data-km-pair-variant]').forEach(option => {
        const active = option.dataset.kmPairVariant === String(variant.id);
        option.classList.toggle('is-selected', active);
        option.setAttribute('aria-pressed', String(active));
      });
      this.querySelector('[data-km-pair-price]').textContent = this.bogoActivityId ? '$0' : variant.price;
      const compare = this.querySelector('[data-km-pair-compare]');
      compare.hidden = this.bogoActivityId ? false : !variant.compare;
      compare.textContent = this.bogoActivityId ? variant.price : variant.compare || '';
      this.updateBogoButton();
      const images = [...this.querySelectorAll('[data-km-pair-gallery-slide]')];
      const index = images.findIndex(image => image.dataset.imageSrc === variant.image);
      if (index >= 0) this.showImage(index);
    }

    showImage(index) {
      const images = [...this.querySelectorAll('[data-km-pair-gallery-slide]')];
      if (!images.length) return;
      this.imageIndex = (index + images.length) % images.length;
      this.querySelector('[data-km-pair-gallery-track]').style.transform = `translate3d(-${this.imageIndex * 100}%,0,0)`;
      images.forEach((image, index) => image.setAttribute('aria-hidden', String(index !== this.imageIndex)));
    }

    updateBogoButton() {
      if (!this.bogoActivityId || !this.button || this.busy || this.rebuildOnly) return;
      const allowed = window.KMCartBogo?.canAdd(this.bogoActivityId, this.dataset.variantId, undefined, this.bogoAnchor);
      this.button.disabled = !allowed;
      this.button.textContent = allowed ? 'Choose free gift' : 'Gift unavailable';
      this.error.hidden = Boolean(allowed);
      if (!allowed) this.error.textContent = 'This gift has been selected or the qualifying gift limit has been reached.';
    }

    async add(refreshOnly = false) {
      const variant = this.variants.find(variant => String(variant.id) === this.dataset.variantId);
      if (this.busy || (!refreshOnly && !this.rebuildOnly && !variant?.available)) return;
      if (this.bogoActivityId && !refreshOnly && !this.rebuildOnly
        && !window.KMCartBogo?.canAdd(this.bogoActivityId, variant.id, undefined, this.bogoAnchor)) {
        this.updateBogoButton();
        return;
      }
      const nativeCart = document.getElementById(this.cartFormId)?.kmCartForm;
      this.error.hidden = true;
      if (!nativeCart?.beginOperation || !nativeCart.buildCart) {
        this.error.textContent = 'Cart is not ready. Please try again.';
        this.error.hidden = false;
        return;
      }
      this.busy = true;
      this.pending = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      const recovery = document.querySelector(`${this.scopeSelector} [data-km-pair-recovery]`);
      const retry = recovery?.querySelector('[data-km-pair-retry]');
      if (retry) retry.disabled = true;
      this.querySelectorAll('[data-km-pair-variant]').forEach(option => { option.disabled = true; });
      const operation = nativeCart.beginOperation('pair-recommendation');
      const analyticsProduct = {handle: this.dataset.productHandle,
        title: this.querySelector('.km-gift-detail__content h3')?.textContent};
      let success = false;
      try {
        if (!refreshOnly && !this.rebuildOnly) {
          if (this.bogoActivityId) {
            const freshCart = window.KMCartState
              ? await window.KMCartState.read(this.root + 'cart.js', {fresh: true})
              : await fetch(this.root + 'cart.js', {credentials: 'same-origin', cache: 'no-store'}).then(response => {
                if (!response.ok) throw new Error('Unable to verify gift eligibility.');
                return response.json();
              });
            if (!window.KMCartBogo.canAdd(this.bogoActivityId, variant.id, freshCart, this.bogoAnchor)) {
              throw new Error('Your cart no longer qualifies for this gift. Please update your selection.');
            }
          }
          const properties = {
            _recommendation_add_id: window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`};
          if (this.bogoActivityId) {
            properties._recommendation_source = 'cart';
            properties._km_bogo_activity = this.bogoActivityId;
            properties._km_bogo_anchor = this.bogoAnchor;
          } else {
            properties.rec_pair_with_order = this.cartFormId === 'CartPageForm' ? 'cart_page' : 'cart_drawer';
          }
          const response = await (window.KMCartState?.fetch || window.fetch.bind(window))(this.root + 'cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: {'Content-Type': 'application/json', Accept: 'application/json'},
            body: JSON.stringify({items: [{id: variant.id, quantity: 1, properties}]})
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
          this.rebuildOnly = true;
          if (this.bogoActivityId) {
            const gift = window.KMCartBogo.getActivity(this.bogoActivityId)?.products.find(product => product.handle === this.dataset.productHandle);
            window.KMCartAnalytics?.commerce?.('add_buy1_get1', {product_id: gift?.id});
          }
          if (!this.bogoActivityId) window.KMCartAnalytics?.recommendationAdded(result, variant.id, analyticsProduct);
        }
        await nativeCart.buildCart();
        this.rebuildOnly = false;
        this.pending = false;
        if (recovery) recovery.hidden = true;
        success = true;
        this.close();
      } catch (error) {
        this.error.textContent = this.rebuildOnly ? 'Item added. Cart update failed. Please retry updating the cart.' : error.message;
        this.error.hidden = false;
        if (recovery) recovery.hidden = false;
      } finally {
        nativeCart.finishOperation(operation, success);
        this.busy = false;
        this.button.disabled = false;
        if (retry) retry.disabled = false;
        this.button.setAttribute('aria-busy', 'false');
        this.button.textContent = this.rebuildOnly ? 'Retry cart update' : 'Add to cart';
        this.querySelectorAll('[data-km-pair-variant]').forEach(option => {
          option.disabled = Boolean(this.rebuildOnly) || !this.variants.find(variant => String(variant.id) === option.dataset.kmPairVariant)?.available;
        });
        if (success) this.updateBogoButton();
      }
    }
  }

  customElements.define('km-cart-pair-dialog', KMCartPairDialog);
})();
